// Capa de autenticación contra la API (Sanctum SPA por cookie).
// Tipos + funciones HTTP. El estado de sesión vive en la cache de TanStack
// Query (ver `src/hooks/useAuth.ts`), no aquí.

import { ApiError, apiFetch } from "./api";

/**
 * Perfil del usuario, tal como lo sirve la API (`UserResource`).
 *
 * Lo que decide es `codigo`, NUNCA `nombre`: el nombre lo renombra el
 * administrador (RF-C3), así que no puede ser la identidad sobre la que se
 * decide qué se ofrece. Es la misma regla del Gate `gestionar-maestros`.
 *
 * `codigo` es `null` en los perfiles creados a mano — solo los tres semilla
 * tienen código —, y un perfil sin código nunca es Super Admin.
 */
export interface PerfilDelUsuario {
  id: number;
  nombre: string;
  codigo: string | null;
}

export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
  /* Siempre presente: `GET /api/user` y `POST /api/login` cargan la relación
     (hay un test de la API que lo fija). Es lo que el webapp usa para decidir
     qué menú dibuja y qué pantallas ofrece. */
  perfil: PerfilDelUsuario;
}

export interface Credentials {
  email: string;
  password: string;
  remember?: boolean;
}

/**
 * Consulta la sesión actual. Sanctum responde 401 cuando no hay sesión: eso NO
 * es un error de la app, es "no autenticado". Lo traducimos a `null` para que la
 * query lo trate como un estado válido y no como un fallo a reintentar.
 */
export async function fetchCurrentUser(): Promise<User | null> {
  try {
    return await apiFetch<User>("/api/user");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/** Autentica y crea la sesión por cookie. Devuelve el usuario logueado. */
export function loginRequest(credentials: Credentials): Promise<{ user: User }> {
  return apiFetch<{ user: User }>("/api/login", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
}

/** Invalida la sesión en el backend (204 sin cuerpo). */
export function logoutRequest(): Promise<void> {
  return apiFetch<void>("/api/logout", { method: "POST" });
}

/**
 * Pide el enlace de recuperación de contraseña (público, sin sesión).
 *
 * La API responde SIEMPRE 200 con el mismo mensaje, exista o no el correo:
 * cualquier divergencia permitiría enumerar cuentas. Por eso el éxito de esta
 * llamada NO significa "el correo salió", significa "la API recibió el
 * pedido" — y la pantalla se lo dice al usuario en esos términos.
 */
export function forgotPasswordRequest(input: { email: string }): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/forgot-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface ResetPasswordInput {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

/** Define una nueva contraseña a partir del enlace de recuperación (público, sin sesión). */
export function resetPasswordRequest(input: ResetPasswordInput): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/reset-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Extrae un mensaje presentable de un ApiError de Laravel.
 * - 419 CSRF irrecuperable: mensaje de SESIÓN (nunca exponemos "CSRF" al usuario).
 * - 422 de validación: `{ message, errors: { campo: [msg] } }` → primer mensaje.
 * - 429: throttle a nivel de ruta (sin cuerpo de validación).
 *
 * El `fallback` es lo ÚNICO que cambia entre pantallas: el 429 del throttle
 * llega como `{"message":"Too Many Attempts."}` —en inglés, del framework— y
 * traducirlo en cada pantalla sería repetir la misma decisión en varios
 * lugares. Por defecto habla del login, que es de donde salió esta función.
 */
export function getAuthErrorMessage(
  error: unknown,
  fallback = "No pudimos iniciar sesión. Revisa tus datos e inténtalo de nuevo.",
): string {
  if (error instanceof ApiError) {
    if (error.status === 419) {
      return "Tu sesión expiró. Vuelve a iniciar sesión.";
    }
    if (error.status === 429) {
      return "Demasiados intentos. Espera un momento e inténtalo de nuevo.";
    }
    try {
      const parsed = JSON.parse(error.body) as {
        message?: string;
        errors?: Record<string, string[]>;
      };
      const firstError = parsed.errors && Object.values(parsed.errors)[0]?.[0];
      if (firstError) return firstError;
      if (parsed.message) return parsed.message;
    } catch {
      // Cuerpo no-JSON (p. ej. HTML de error): caemos al mensaje genérico.
    }
  }
  return fallback;
}
